import { Chip, styled, useTheme } from "@mui/material";

import { WithdrawalRequestStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

interface WithdrawalStatusChipProps {
  status: WithdrawalRequestStatus;
}

const StyledChip = styled(Chip, { shouldForwardProp: isPropValid })<{
  $backgroundColor: string;
  $textColor: string;
}>`
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  font-size: 12px;
  font-weight: 400;

  .MuiChip-label {
    padding: ${(props) => props.theme.spacing(0, Spacing.M)};
  }
`;

const STATUS_LABELS: Record<
  WithdrawalRequestStatus,
  (t: ReturnType<typeof useResource>["t"]) => string
> = {
  [WithdrawalRequestStatus.Pending]: (t) =>
    t(Resources.WithdrawalApprovals.StatusPending),
  [WithdrawalRequestStatus.Approved]: (t) =>
    t(Resources.WithdrawalApprovals.StatusApproved),
  [WithdrawalRequestStatus.Rejected]: (t) =>
    t(Resources.WithdrawalApprovals.StatusRejected),
  [WithdrawalRequestStatus.Expired]: (t) =>
    t(Resources.WithdrawalApprovals.StatusExpired),
};

export const WithdrawalStatusChip = ({
  status,
}: WithdrawalStatusChipProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const colorMap: Record<WithdrawalRequestStatus, string> = {
    [WithdrawalRequestStatus.Pending]: theme.palette.warning.dark,
    [WithdrawalRequestStatus.Approved]: theme.palette.success.dark,
    [WithdrawalRequestStatus.Rejected]: theme.palette.error.dark,
    [WithdrawalRequestStatus.Expired]: theme.palette.grey[700],
  };

  return (
    <StyledChip
      label={STATUS_LABELS[status](t)}
      $backgroundColor={colorMap[status]}
      $textColor={theme.palette.text.primary}
    />
  );
};
